// ── Achievement Definitions ────────────────────────────────────────────────────

export const ACHIEVEMENT_DEFS = [
  {
    id: 'first_puzzle',
    title: 'First Steps',
    desc: 'Complete your first puzzle',
    icon: '🌱',
    check: (stats) => stats.totalCompleted >= 1,
  },
  {
    id: 'curious_learner',
    title: 'Curious Learner',
    desc: 'Complete 5 puzzles',
    icon: '📚',
    check: (stats) => stats.totalCompleted >= 5,
  },
  {
    id: 'dedicated_student',
    title: 'Dedicated Student',
    desc: 'Complete 10 puzzles',
    icon: '🎓',
    check: (stats) => stats.totalCompleted >= 10,
  },
  {
    id: 'speed_demon',
    title: 'Speed Demon',
    desc: 'Complete a puzzle with 15+ seconds remaining',
    icon: '⚡',
    check: (stats) => stats.bestTimeRemaining >= 15,
  },
  {
    id: 'efficiency_master',
    title: 'Efficiency Master',
    desc: 'Complete a puzzle with 10+ moves remaining',
    icon: '🎯',
    check: (stats) => stats.bestMovesRemaining >= 10,
  },
  {
    id: 'basic_scholar',
    title: 'Basic Scholar',
    desc: 'Complete all Basic mode puzzles',
    icon: '🏅',
    check: (stats) => stats.easyCompleted >= 20,
  },
  {
    id: 'advanced_master',
    title: 'Advanced Master',
    desc: 'Complete all Advanced mode puzzles',
    icon: '🥇',
    check: (stats) => stats.hardCompleted >= 20,
  },
  {
    id: 'puzzle_champion',
    title: 'Puzzle Champion',
    desc: 'Complete all 40 puzzles',
    icon: '👑',
    check: (stats) => stats.totalCompleted >= 40,
  },
  {
    id: 'social_player',
    title: 'Social Player',
    desc: 'Share a puzzle with a friend',
    icon: '🔗',
    check: (stats) => stats.sharedCount >= 1,
  },
  {
    id: 'challenger',
    title: 'Challenger',
    desc: 'Complete a puzzle from a friend\'s challenge link',
    icon: '⚔️',
    check: (stats) => stats.challengesCompleted >= 1,
  },
];

// ── Stats helpers ──────────────────────────────────────────────────────────────

const STATS_KEY = 'wq_achievement_stats';
const UNLOCKED_KEY = 'wq_achievements_unlocked';

function loadStats() {
  try {
    return JSON.parse(localStorage.getItem(STATS_KEY)) ?? {};
  } catch {
    return {};
  }
}

function saveStats(stats) {
  localStorage.setItem(STATS_KEY, JSON.stringify(stats));
}

export function loadUnlocked() {
  try {
    return JSON.parse(localStorage.getItem(UNLOCKED_KEY)) ?? [];
  } catch {
    return [];
  }
}

function saveUnlocked(ids) {
  localStorage.setItem(UNLOCKED_KEY, JSON.stringify(ids));
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Rebuild stats from localStorage completion data and any override values.
 * Call after each puzzle completion to keep stats fresh.
 *
 * @param {object} override – partial stats to merge in (e.g. bestTimeRemaining from just-won puzzle)
 * @returns {string[]} array of newly unlocked achievement IDs
 */
export function updateAchievements(override = {}) {
  // Count completions from localStorage
  let totalCompleted = 0;
  let easyCompleted = 0;
  let hardCompleted = 0;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('completed-') && localStorage.getItem(key) === 'true') {
      totalCompleted++;
      const id = parseInt(key.replace('completed-', ''));
      if (id >= 1001 && id <= 1020) easyCompleted++;
      if (id >= 2001 && id <= 2020) hardCompleted++;
    }
  }

  const existing = loadStats();
  const stats = {
    totalCompleted,
    easyCompleted,
    hardCompleted,
    bestTimeRemaining: existing.bestTimeRemaining ?? 0,
    bestMovesRemaining: existing.bestMovesRemaining ?? 0,
    sharedCount: existing.sharedCount ?? 0,
    challengesCompleted: existing.challengesCompleted ?? 0,
    ...override,
    // Merge maxima (don't overwrite a higher value with a lower one)
    bestTimeRemaining: Math.max(existing.bestTimeRemaining ?? 0, override.bestTimeRemaining ?? 0),
    bestMovesRemaining: Math.max(existing.bestMovesRemaining ?? 0, override.bestMovesRemaining ?? 0),
  };
  saveStats(stats);

  const unlocked = new Set(loadUnlocked());
  const newlyUnlocked = [];

  for (const def of ACHIEVEMENT_DEFS) {
    if (!unlocked.has(def.id) && def.check(stats)) {
      unlocked.add(def.id);
      newlyUnlocked.push(def.id);
    }
  }

  if (newlyUnlocked.length > 0) {
    saveUnlocked([...unlocked]);
  }

  return newlyUnlocked;
}

/** Increment the share count and re-evaluate achievements. */
export function recordShare() {
  const stats = loadStats();
  stats.sharedCount = (stats.sharedCount ?? 0) + 1;
  saveStats(stats);
  return updateAchievements();
}

/** Increment challenge completion count and re-evaluate achievements. */
export function recordChallengeComplete() {
  const stats = loadStats();
  stats.challengesCompleted = (stats.challengesCompleted ?? 0) + 1;
  saveStats(stats);
  return updateAchievements();
}
